import React, { useState, useEffect } from 'react';

// Recharts Components
import {
  BarChart, Bar, 
  LineChart, Line,
  PieChart, Pie, Cell,
  AreaChart, Area,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Lucide React Icons
import { 
  TrendingUp, TrendingDown, BarChart3, Activity, Target,
  Building2, Users, Briefcase, Award, Shield,
  DollarSign, Package,
  ChevronRight, ChevronDown, Menu, X, Plus, Minus, 
  Filter, Search, Settings, Eye, Download, RefreshCw,
  AlertTriangle, Bell, CheckCircle, XCircle, Clock, Star,
  Lock, Unlock, ArrowUpRight,
  Database, Cloud, Cpu, HardDrive, Wifi, Battery,
  Smartphone, Tablet, Monitor,
  Mail, Phone, MessageSquare, Video, Share2, Bookmark, Heart,
  MapPin, Calendar, Globe, Brain, FileText, Layers, Zap
} from 'lucide-react';

// Constants
const API_URL = "https://backend.buminu.co.id";
const PERIOD_MAP = {
  'bulan': 'month',
  'kuartal': 'quarter', 
  'tahun': 'year'
};

// Utility Functions
const formatRupiah = (amount) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatNumber = (num) => {
  return new Intl.NumberFormat('id-ID').format(num);
};

const getStatusColor = (status) => {
  const colors = {
    'excellent': 'text-emerald-700 bg-emerald-100 border-emerald-200',
    'good': 'text-blue-700 bg-blue-100 border-blue-200',
    'warning': 'text-amber-700 bg-amber-100 border-amber-200',
    'critical': 'text-red-700 bg-red-100 border-red-200'
  };
  return colors[status] || 'text-gray-700 bg-gray-100 border-gray-200';
};

const getRiskColor = (level) => {
  const colors = {
    'Low': 'text-emerald-700 bg-emerald-50',
    'Medium': 'text-amber-700 bg-amber-50',
    'High': 'text-red-700 bg-red-50'
  };
  return colors[level] || 'text-gray-700 bg-gray-50';
};

const PremiumIntegratedDashboard = () => {
  // State Management
  const [userRole, setUserRole] = useState('investor');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('bulan');
  const [selectedCity, setSelectedCity] = useState('all');
  const [activeView, setActiveView] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Legal & Security State
  const [showLegalDisclaimer, setShowLegalDisclaimer] = useState(false);
  const [isAgreementAccepted, setIsAgreementAccepted] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [lastReminderTime, setLastReminderTime] = useState(null);
  
  // Business Data State
  const [businessName, setBusinessName] = useState('');
  const [businessId, setBusinessId] = useState(null);
  const [locations, setLocations] = useState([]);
  const [companiesData, setCompaniesData] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  
  // Sales Data State
  const [salesData, setSalesData] = useState({
    totalSales: 0,
    totalProfit: 0,
    totalAssets: 0,
    totalEmployees: 0,
    growth: 0
  });

  // Responsive State
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Navigation Menus
  const navigationMenus = {
    'Core Analytics': [
      { id: 'overview', name: 'Overview', icon: BarChart3, premium: false },
      { id: 'companies', name: 'Locations', icon: Building2, premium: false },
      { id: 'financial', name: 'Financial', icon: DollarSign, premium: false },
      { id: 'assets', name: 'Assets', icon: Package, premium: false }
    ],
    'Strategic': [
      { id: 'portfolio', name: 'Portfolio', icon: PieChart, premium: true },
      { id: 'risk', name: 'Risk', icon: Shield, premium: true },
      { id: 'strategic', name: 'Strategic', icon: Target, premium: true },
      { id: 'market', name: 'Market', icon: Globe, premium: true }
    ],
    'Governance': [
      { id: 'compliance', name: 'Compliance', icon: Award, premium: true },
      { id: 'esg', name: 'ESG', icon: Layers, premium: true },
      { id: 'board', name: 'Board', icon: FileText, premium: true },
      { id: 'governance', name: 'Governance', icon: Lock, premium: true }
    ],
    'Advanced': [
      { id: 'predictive', name: 'Predictive', icon: Brain, premium: true },
      { id: 'benchmarking', name: 'Benchmark', icon: Activity, premium: true },
      { id: 'cashflow', name: 'Cash Flow', icon: Zap, premium: true },
      { id: 'hrm', name: 'HR', icon: Users, premium: false }
    ]
  };

  // API Functions
  const fetchBusinessData = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/businesses`);
      
      if (!response.ok) throw new Error('Failed to fetch business data');
      
      const result = await response.json();
      
      if (result.success && result.data?.length > 0) {
        const businessData = result.data[0];
        setBusinessName(businessData.name);
        setBusinessId(businessData.id);
      } else {
        setBusinessName('HOLDING DASHBOARD');
      }
    } catch (error) {
      setBusinessName('HOLDING DASHBOARD');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocations = async () => {
    if (!businessId) return;

    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/business/${businessId}/locations`);
      
      if (!response.ok) throw new Error('Failed to fetch locations data');
      
      const result = await response.json();
      if (result.success && result.data) setLocations(result.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompaniesData = async () => {
    if (!businessId) return;

    try {
      setIsLoadingCompanies(true);

      const response = await fetch(`${API_URL}/api/business/${businessId}/locations`);
      if (!response.ok) throw new Error('Failed to fetch locations');

      const result = await response.json();
      if (!result.success || !result.data) {
        setCompaniesData([]);
        return;
      }

      const locations = result.data;
      const apiPeriod = PERIOD_MAP[selectedPeriod] || 'month';

      const salesPromises = locations.map(async (location) => {
        try {
          const url = `${API_URL}/api/transactions/total-sales?location_id=${location.id}&period=${apiPeriod}`;
          const salesResponse = await fetch(url);
          if (!salesResponse.ok) throw new Error('Failed to fetch sales');

          const salesResult = await salesResponse.json();
          return salesResult.data?.total_sales || 0;
        } catch (err) {
          console.error(`Error fetching sales for location ${location.id}:`, err);
          return 0;
        }
      });

      const salesData = await Promise.all(salesPromises);

      const transformedData = locations.map((location, index) => {
		const sales = salesData[index];
		const profit = Math.floor(sales * 0.25);
		const assets = Math.floor(Math.random() * 50000000) + 8000000;
  
		const calculateROI = (profit, investment) => {
			if (!investment || investment === 0) return 0;
			return (profit / investment) * 100;
		};
  
		return {
			id: location.id,
			name: location.name || `Location ${location.id}`,
			city: location.city || 'Unknown City',
			type: location.type || 'Standard',
			sales,
			profit,
			assets,
			investment: assets,
			projects: Math.floor(Math.random() * 5),
			employees: Math.floor(Math.random() * 50) + 5,
			status: ['excellent', 'good', 'warning', 'critical'][Math.floor(Math.random() * 4)],
			riskLevel: ['Low', 'Medium', 'High'][Math.floor(Math.random() * 3)],
			compliance: Math.floor(Math.random() * 40) + 60,
			esgScore: Math.floor(Math.random() * 30) + 70,
			marketShare: Math.floor(Math.random() * 15) + 5,
			cashFlow: Math.floor(Math.random() * 20000000) + 5000000,
			roi: calculateROI(profit, assets),
			growth: Math.floor(Math.random() * 40) - 20
		};
	});

      setCompaniesData(transformedData);
    } catch (error) {
      console.error('Error refreshing companies data:', error);
      setCompaniesData([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const fetchDashboardData = async () => {
    if (!businessId || locations.length === 0) return;

    try {
      setIsLoading(true);
      
      let url = `${API_URL}/api/transactions/dashboard/summary?`;
      
      if (selectedCompany && selectedCompany !== 'all') {
        url += `location_id=${selectedCompany}&`;      
      } 
      
      const periodValue = PERIOD_MAP[selectedPeriod] || 'month';
      url += `period=${periodValue}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const result = await response.json();
      
      if (result.success) {
        const dashboardData = result.data;
        setSalesData(prev => ({
          ...prev,
          totalSales: parseFloat(dashboardData.total_sales) || 0,
          totalProfit: parseFloat(dashboardData.net_profit) || 0,
          growth: 15.8
        }));
      } else {
        setSalesData(prev => ({
          ...prev,
          totalSales: 0,
          totalProfit: 0
        }));
      }
    } catch (error) {
      setSalesData(prev => ({
        ...prev,
        totalSales: 0,
        totalProfit: 0
      }));
    } finally {
      setIsLoading(false);      
    }
  };

  const refreshCompaniesData = async () => {
    await fetchCompaniesData();
  };

  // Effects
  useEffect(() => {
    fetchBusinessData();
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [businessId]);

  useEffect(() => {
    fetchCompaniesData();
  }, [businessId, selectedPeriod]);

  useEffect(() => {
    if (businessId && locations.length > 0) {
      fetchDashboardData();
    }
  }, [selectedCompany, selectedPeriod, businessId, locations.length]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const reminderInterval = setInterval(() => {
      setShowReminderModal(true);
      setLastReminderTime(new Date().toLocaleTimeString());
    }, 2 * 60 * 60 * 1000);

    return () => clearInterval(reminderInterval);
  }, []);

  useEffect(() => {
    let activityTimer;
    let warningTimer;
    
    const resetTimers = () => {
      clearTimeout(activityTimer);
      clearTimeout(warningTimer);
      
      warningTimer = setTimeout(() => {
        alert('⚠️ PERINGATAN: Sesi akan berakhir dalam 5 menit karena tidak ada aktivitas.');
      }, 25 * 60 * 1000);
      
      activityTimer = setTimeout(() => {
        alert('🔒 SESI BERAKHIR: Dashboard ditutup otomatis karena tidak ada aktivitas selama 30 menit.');
        window.location.reload();
      }, 30 * 60 * 1000);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const logActivity = (event) => {
      console.log(`[SECURITY LOG] ${new Date().toISOString()} - User Activity: ${event.type}`);
      resetTimers();
    };

    events.forEach(event => {
      document.addEventListener(event, logActivity, true);
    });

    resetTimers();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, logActivity, true);
      });
      clearTimeout(activityTimer);
      clearTimeout(warningTimer);
    };
  }, [userRole, activeView]);

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      if (e.key === 'F12' || 
          (e.ctrlKey && e.shiftKey && e.key === 'I') ||
          (e.ctrlKey && e.key === 'u') ||
          e.key === 'PrintScreen') {
        e.preventDefault();
        alert('⚠️ PERINGATAN KEAMANAN: Screenshot dan inspect element dilarang!');
      }
    };

    const handleCopy = (e) => {
      e.preventDefault();
      alert('🚫 COPY DISABLED: Penyalinan konten dashboard dilarang.');
    };

    const handleDrag = (e) => {
      e.preventDefault();
      alert('🚫 DRAG DISABLED: Transfer data dari dashboard dilarang.');
    };

    const handleSelectStart = (e) => {
      if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCopy);
    document.addEventListener('dragstart', handleDrag);
    document.addEventListener('selectstart', handleSelectStart);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCopy);
      document.removeEventListener('dragstart', handleDrag);
      document.removeEventListener('selectstart', handleSelectStart);
    };
  }, []);

  // Data Processing
  const filteredCompanies = companiesData.filter(company => 
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalSales = filteredCompanies.reduce((sum, company) => sum + company.sales, 0);
  const totalProfit = filteredCompanies.reduce((sum, company) => sum + company.profit, 0);
  const totalAssets = filteredCompanies.reduce((sum, company) => sum + company.assets, 0);
  const totalProjects = filteredCompanies.reduce((sum, company) => sum + company.projects, 0);
  const totalEmployees = filteredCompanies.reduce((sum, company) => sum + company.employees, 0);
  const avgROI = filteredCompanies.length > 0 ? filteredCompanies.reduce((sum, company) => sum + company.roi, 0) / filteredCompanies.length : 0;
  const avgESGScore = filteredCompanies.length > 0 ? filteredCompanies.reduce((sum, company) => sum + company.esgScore, 0) / filteredCompanies.length : 0;

  // Chart Data
  const salesTrendData = [
    { month: 'Jan', sales: totalSales * 0.75, profit: totalProfit * 0.7, target: totalSales * 0.8 },
    { month: 'Feb', sales: totalSales * 0.82, profit: totalProfit * 0.78, target: totalSales * 0.85 },
    { month: 'Mar', sales: totalSales * 0.88, profit: totalProfit * 0.85, target: totalSales * 0.9 },
    { month: 'Apr', sales: totalSales * 0.93, profit: totalProfit * 0.9, target: totalSales * 0.95 },
    { month: 'Mei', sales: totalSales, profit: totalProfit, target: totalSales },
    { month: 'Jun', sales: totalSales * 1.08, profit: totalProfit * 1.05, target: totalSales * 1.05 },
  ];

  const riskAnalysisData = [
    { subject: 'Market Risk', A: 75, B: 85, fullMark: 100 },
    { subject: 'Credit Risk', A: 82, B: 78, fullMark: 100 },
    { subject: 'Operational Risk', A: 68, B: 90, fullMark: 100 },
    { subject: 'Liquidity Risk', A: 88, B: 72, fullMark: 100 },
    { subject: 'Regulatory Risk', A: 79, B: 88, fullMark: 100 },
    { subject: 'ESG Risk', A: 85, B: 80, fullMark: 100 }
  ];

  const portfolioDistribution = [
    { name: 'Premium Brands', value: 45, color: '#059669' },
    { name: 'Standard Products', value: 35, color: '#0891b2' },
    { name: 'Economic Segment', value: 20, color: '#7c2d12' }
  ];

  // Component Rendering
  const renderContent = () => {
    switch (activeView) {
      case 'overview':
        return <OverviewView 
          salesData={salesData} 
          isLoading={isLoading}
          totalSales={totalSales}
          totalProfit={totalProfit}
          totalAssets={totalAssets}
          totalEmployees={totalEmployees}
          avgROI={avgROI}
          avgESGScore={avgESGScore}
          filteredCompanies={filteredCompanies}
          isMobile={isMobile}
          salesTrendData={salesTrendData}
          riskAnalysisData={riskAnalysisData}
          portfolioDistribution={portfolioDistribution}
          formatRupiah={formatRupiah}
          formatNumber={formatNumber}
        />;
      
      case 'companies':
        return <CompaniesView 
          filteredCompanies={filteredCompanies}
          isLoadingCompanies={isLoadingCompanies}
          businessId={businessId}
          refreshCompaniesData={refreshCompaniesData}
          formatRupiah={formatRupiah}
          getStatusColor={getStatusColor}
        />;
      
      case 'risk':
        return <RiskView 
          filteredCompanies={filteredCompanies}
          formatRupiah={formatRupiah}
          getRiskColor={getRiskColor}
        />;
      
      default:
        return (
          <div className="bg-white/80 backdrop-blur-lg p-6 rounded-xl shadow-lg border border-white/20 text-center">
            <Brain className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Feature in Development</h3>
            <p className="text-gray-500 text-sm">This advanced feature is coming soon!</p>
          </div>
        );
    }
  };

  // Event Handlers
  const handleRefreshAll = async () => {
    setIsLoading(true);
    await Promise.all([fetchDashboardData(), refreshCompaniesData()]);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-emerald-900">
      {/* Legal Disclaimer Modal */}
      {showLegalDisclaimer && (
        <LegalDisclaimerModal 
          isAgreementAccepted={isAgreementAccepted}
          setIsAgreementAccepted={setIsAgreementAccepted}
          setShowLegalDisclaimer={setShowLegalDisclaimer}
        />
      )}

      {/* Reminder Modal */}
      {showReminderModal && (
        <ReminderModal 
          lastReminderTime={lastReminderTime}
          setShowReminderModal={setShowReminderModal}
        />
      )}

      {/* Watermark Security */}
      <div className="fixed inset-0 pointer-events-none z-10 opacity-5">
        <div className="absolute inset-0 bg-repeat" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Ctext x='10' y='50' fill='white' font-size='8' transform='rotate(-45 50 50)'%3ERAHASIA DAGANG - ${userRole.toUpperCase()}%3C/text%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }}></div>
      </div>

      {/* Header */}      
	  <Header 
		businessName={businessName}
		isLoading={isLoading}
		userRole={userRole}
		setUserRole={setUserRole}
		mobileMenuOpen={mobileMenuOpen}
		setMobileMenuOpen={setMobileMenuOpen}
		isMobile={isMobile}
		notificationOpen={notificationOpen}
		setNotificationOpen={setNotificationOpen}
	  />

      {/* Navigation */}
      <Navigation 
        navigationMenus={navigationMenus}
        activeView={activeView}
        setActiveView={setActiveView}
        userRole={userRole}
        isMobile={isMobile}
      />

      {/* Filters */}
      <Filters 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedCompany={selectedCompany}
        setSelectedCompany={setSelectedCompany}
        selectedPeriod={selectedPeriod}
        setSelectedPeriod={setSelectedPeriod}
        locations={locations}
        isLoading={isLoading}
        handleRefreshAll={handleRefreshAll}
        isMobile={isMobile}
      />

      {/* Main Content */}
      <main className="px-3 sm:px-4 py-4 sm:py-6">
        {renderContent()}
        
        {/* Security Footer */}
        <SecurityFooter />
      </main>

      {/* Loading Overlay */}
      {isLoading && <LoadingOverlay />}

      {/* Security Indicators */}
      <SecurityIndicators userRole={userRole} />
    </div>
  );
};

// Sub-Components
const LegalDisclaimerModal = ({ isAgreementAccepted, setIsAgreementAccepted, setShowLegalDisclaimer }) => (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
    <div className="bg-gradient-to-br from-red-900 via-red-800 to-red-900 rounded-xl shadow-2xl border-2 border-red-500 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
      <div className="bg-gradient-to-r from-red-600 to-red-700 p-4 rounded-t-xl border-b-2 border-red-400">
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Shield className="w-6 h-6 text-red-100" />
            <div className="absolute inset-0 animate-ping">
              <Shield className="w-6 h-6 text-red-300 opacity-75" />
            </div>
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">⚠️ PERNYATAAN KERAHASIAAN</h2>
            <p className="text-red-100 text-xs">DASHBOARD RAHASIA DAGANG</p>
          </div>
        </div>
      </div>

      <div className="p-4 text-white space-y-4">
        <div className="bg-red-800/50 p-3 rounded-lg border border-red-400">
          <h3 className="font-bold text-sm mb-2 text-red-100">🚨 LARANGAN KERAS</h3>
          <div className="space-y-1 text-xs">
            <p className="flex items-start space-x-1">
              <span className="text-red-300">•</span>
              <span>DILARANG menyebarkan, menyalin, atau mendistribusikan informasi</span>
            </p>
            <p className="flex items-start space-x-1">
              <span className="text-red-300">•</span>
              <span>DILARANG mengambil screenshot, foto, atau recording</span>
            </p>
            <p className="flex items-start space-x-1">
              <span className="text-red-300">•</span>
              <span>DILARANG membagikan akses login</span>
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
          <button 
            onClick={() => window.history.back()}
            className="flex-1 bg-gradient-to-r from-gray-600 to-gray-700 text-white py-2 px-4 rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all duration-300 text-sm font-medium"
          >
            ❌ Tolak & Keluar
          </button>
          <button 
            onClick={() => isAgreementAccepted && setShowLegalDisclaimer(false)}
            disabled={!isAgreementAccepted}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-300 ${
              isAgreementAccepted 
                ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white hover:from-emerald-700 hover:to-emerald-800' 
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            ✅ Setuju & Lanjutkan
          </button>
        </div>
      </div>
    </div>
  </div>
);

const ReminderModal = ({ lastReminderTime, setShowReminderModal }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[90] flex items-center justify-center p-4">
    <div className="bg-gradient-to-br from-amber-900 via-amber-800 to-amber-900 rounded-xl shadow-2xl border-2 border-amber-500 w-full max-w-md mx-4">
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-3 rounded-t-xl">
        <div className="flex items-center space-x-2">
          <Bell className="w-5 h-5 text-amber-100 animate-bounce" />
          <div>
            <h3 className="text-base sm:text-lg font-bold text-white">🔔 PENGINGAT KEAMANAN</h3>
            <p className="text-amber-100 text-xs">Pemantauan Kepatuhan</p>
          </div>
        </div>
      </div>
      
      <div className="p-4 text-white space-y-3">
        <div className="bg-amber-800/50 p-3 rounded-lg border border-amber-400">
          <p className="text-amber-100 text-xs">
            <strong>REMINDER:</strong> Anda masih terikat komitmen kerahasiaan. Seluruh aktivitas dashboard tetap dalam pengawasan sistem keamanan.
          </p>
        </div>
        
        <div className="text-center space-y-2">
          <p className="text-xs text-amber-200">
            Waktu: {lastReminderTime}
          </p>
          <button 
            onClick={() => setShowReminderModal(false)}
            className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 text-white py-2 px-4 rounded-lg hover:from-emerald-700 hover:to-blue-800 transition-all duration-300 text-sm font-medium"
          >
            ✅ Saya Paham
          </button>
        </div>
      </div>
    </div>
  </div>
);

const Header = ({ 
  businessName, 
  isLoading, 
  userRole, 
  setUserRole, 
  mobileMenuOpen, 
  setMobileMenuOpen, 
  isMobile,
  notificationOpen,
  setNotificationOpen 
}) => (
  <header className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
    {/* Security Banner */}
    <div className="bg-gradient-to-r from-red-600 via-red-700 to-red-600 text-white py-1 px-4 text-center relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-red-500 to-red-600 animate-pulse opacity-30"></div>
      <div className="relative z-10 flex items-center justify-center space-x-2 text-xs sm:text-sm font-medium">
        <Shield className="w-3 h-3 sm:w-4 sm:h-4 animate-pulse" />
        <span className="truncate">🔒 RAHASIA DAGANG - CONFIDENTIAL</span>
        {!isMobile && (
          <>
            <span className="hidden sm:inline">•</span>
            <span className="hidden sm:inline">📵 NO SCREENSHOT/RECORDING</span>
            <span className="hidden md:inline">•</span>
            <span className="hidden md:inline">⚖️ UU No. 30/2000</span>
          </>
        )}
        <Lock className="w-3 h-3 sm:w-4 sm:h-4 animate-pulse" />
      </div>
    </div>
    
    <div className="px-3 sm:px-4 py-3">
      <div className="flex items-center justify-between">
        {/* Logo & Title */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-lg flex items-center justify-center">
            <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          <div>
            <h1 className="text-base sm:text-lg font-bold text-white">
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Loading...</span>
                </div>
              ) : (
                `${businessName} HOLDING DASHBOARD`
              )}
            </h1>
            {!isMobile && (
              <p className="text-emerald-200 text-xs">Integrated Investor Dashboard</p>
            )}
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="lg:hidden p-1 text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Desktop Controls */}
        <div className="hidden lg:flex items-center space-x-2 sm:space-x-3">
          <select 
            value={userRole} 
            onChange={(e) => setUserRole(e.target.value)}
            className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-xs sm:text-sm focus:ring-2 focus:ring-emerald-500 backdrop-blur-lg"
          >
            <option value="investor" className="text-gray-900">👤 Investor</option>
            <option value="holding" className="text-gray-900">🏢 Holding Admin</option>
          </select>
          
          <div className="relative">
            <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-white cursor-pointer hover:text-emerald-300 transition-colors" />
            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
          </div>
          
          <button className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-lg hover:from-emerald-700 hover:to-blue-700 transition-all duration-300 shadow">
            <Download className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline text-xs sm:text-sm">Export</span>
          </button>
        </div>
      </div>
    </div>

    {/* Mobile Menu */}
    {mobileMenuOpen && (
      <div className="lg:hidden bg-black/30 backdrop-blur-lg border-t border-white/10 p-3">
        <div className="space-y-3">
          <select 
            value={userRole} 
            onChange={(e) => setUserRole(e.target.value)}
            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
          >
            <option value="investor" className="text-gray-900">👤 Investor Access</option>
            <option value="holding" className="text-gray-900">🏢 Holding Admin</option>
          </select>
          
          <button 
            onClick={() => setNotificationOpen(!notificationOpen)}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-lg"
          >
            <Bell className="w-4 h-4" />
            <span>Notifications</span>
          </button>
          
          <button className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-lg">
            <Download className="w-4 h-4" />
            <span>Export Reports</span>
          </button>
        </div>
      </div>
    )}
  </header>
);

const Navigation = ({ navigationMenus, activeView, setActiveView, userRole, isMobile }) => (
  <nav className="bg-black/10 backdrop-blur-lg border-b border-white/10 sticky top-16 lg:top-[4.5rem] z-40">
    <div className="px-2 sm:px-3">
      <div className="flex overflow-x-auto scrollbar-hide space-x-1 py-1">
        {Object.entries(navigationMenus).map(([category, menus]) => (
          <div key={category} className="flex space-x-1">
            {menus.map((menu) => (
              <button
                key={menu.id}
                onClick={() => setActiveView(menu.id)}
                className={`flex items-center space-x-1 px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap min-w-fit ${
                  activeView === menu.id
                    ? 'bg-gradient-to-r from-emerald-600 to-blue-600 text-white shadow'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                } ${menu.premium && userRole !== 'holding' ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={menu.premium && userRole !== 'holding'}
              >
                <menu.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{menu.name}</span>
                {menu.premium && <Star className="w-2 h-2 sm:w-3 sm:h-3 text-amber-400" />}
              </button>
            ))}
            <div className="w-px bg-white/20 mx-1 my-1" />
          </div>
        ))}
      </div>
    </div>
  </nav>
);

const Filters = ({ 
  searchTerm, 
  setSearchTerm, 
  selectedCompany, 
  setSelectedCompany, 
  selectedPeriod, 
  setSelectedPeriod, 
  locations, 
  isLoading, 
  handleRefreshAll,
  isMobile 
}) => (
  <div className="bg-black/5 backdrop-blur-lg border-b border-white/10 px-3 sm:px-4 py-3">
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      <div className="flex items-center space-x-1 text-white/70">
        <Filter className="w-3 h-3 sm:w-4 sm:h-4" />
        <span className="text-xs sm:text-sm font-medium">Filters:</span>
      </div>
      
      {/* Search Input */}
      <div className="relative">
        <Search className="w-3 h-3 sm:w-4 sm:h-4 text-white/70 absolute left-2 top-1/2 transform -translate-y-1/2" />
        <input
          type="text"
          placeholder="Search locations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8 pr-3 py-1.5 bg-white/10 border border-white/20 rounded-md text-white text-xs sm:text-sm placeholder-white/50 focus:ring-2 focus:ring-emerald-500 backdrop-blur-lg min-w-[150px]"
        />
      </div>
      
      <select 
        value={selectedCompany} 
        onChange={(e) => setSelectedCompany(e.target.value)}
        className="px-2 py-1 sm:px-3 sm:py-1.5 bg-white/10 border border-white/20 rounded-md text-white text-xs sm:text-sm focus:ring-2 focus:ring-emerald-500 backdrop-blur-lg min-w-0"
      >
        <option value="all" className="text-gray-900">All Locations</option>
        {locations.map(location => (
          <option key={location.id} value={location.id} className="text-gray-900">
            {location.name}
          </option>
        ))}
      </select>
      
      <select 
        value={selectedPeriod} 
        onChange={(e) => setSelectedPeriod(e.target.value)}
        className="px-2 py-1 sm:px-3 sm:py-1.5 bg-white/10 border border-white/20 rounded-md text-white text-xs sm:text-sm focus:ring-2 focus:ring-emerald-500 backdrop-blur-lg min-w-0"
      >
        <option value="bulan" className="text-gray-900">This Month</option>
        <option value="kuartal" className="text-gray-900">This Quarter</option>
        <option value="tahun" className="text-gray-900">This Year</option>
      </select>

      <button 
        onClick={handleRefreshAll}
        className="flex items-center space-x-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-white/10 border border-white/20 rounded-md text-white text-xs sm:text-sm hover:bg-white/20 transition-all duration-300"
      >
        <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 ${isLoading ? 'animate-spin' : ''}`} />
        <span className="hidden sm:inline">Refresh</span>
      </button>

      {/* Clear Search Button */}
      {searchTerm && (
        <button
          onClick={() => setSearchTerm('')}
          className="flex items-center space-x-1 px-2 py-1 bg-white/10 border border-white/20 rounded-md text-white text-xs hover:bg-white/20 transition-all duration-300"
        >
          <X className="w-3 h-3" />
          <span>Clear</span>
        </button>
      )}
    </div>
  </div>
);

const SecurityFooter = () => (
  <div className="mt-8 bg-gradient-to-r from-red-900/50 via-red-800/50 to-red-900/50 backdrop-blur-lg rounded-xl border border-red-500/30 p-4">
    <div className="flex items-center space-x-2 sm:space-x-3 text-red-100">
      <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-400 animate-pulse" />
      <div className="flex-1">
        <h4 className="font-bold text-sm sm:text-base">⚠️ PERINGATAN KEAMANAN</h4>
        <p className="text-red-200 text-xs sm:text-sm mt-1">
          Seluruh aktivitas dalam dashboard ini dipantau dan direkam. Penyalahgunaan informasi rahasia dagang 
          akan dikenakan sanksi hukum sesuai <strong>UU No. 30 Tahun 2000</strong>.
        </p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-red-300">
          <span>🕐 Session ID: {Math.random().toString(36).substr(2, 9)}</span>
          <span>📍 IP Monitoring: Active</span>
          <span>🔍 Audit Trail: Enabled</span>
        </div>
      </div>
    </div>
  </div>
);

const LoadingOverlay = () => (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
    <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 max-w-xs w-full mx-4">
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-3">
          <RefreshCw className="w-6 h-6 text-emerald-400 animate-spin" />
          <div>
            <p className="text-white font-medium text-sm">Updating Dashboard</p>
            <p className="text-white/70 text-xs">Fetching latest data...</p>
          </div>
        </div>
        
        <div className="bg-red-900/30 border border-red-500/50 rounded-md p-2 mt-3">
          <div className="flex items-center space-x-1">
            <Lock className="w-3 h-3 text-red-400" />
            <p className="text-red-200 text-xs">
              Data terlindungi enkripsi end-to-end
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SecurityIndicators = ({ userRole }) => (
  <>
    <div className="fixed bottom-4 left-4 bg-gradient-to-r from-red-600 to-red-700 text-white px-3 py-1 rounded-md shadow text-xs font-medium z-40 animate-pulse">
      🚫 Screenshot Detection: ACTIVE
    </div>

    <div className="fixed bottom-4 right-4 bg-black/30 backdrop-blur-lg text-white px-2 py-1 rounded-md text-xs font-medium z-40">
      👤 {userRole.toUpperCase()} | 🕐 {new Date().toLocaleTimeString()}
    </div>
  </>
);

// View Components
const OverviewView = ({
  salesData,
  isLoading,
  totalSales,
  totalProfit,
  totalAssets,
  totalEmployees,
  avgROI,
  avgESGScore,
  filteredCompanies,
  isMobile,
  salesTrendData,
  riskAnalysisData,
  portfolioDistribution,
  formatRupiah,
  formatNumber
}) => (
  <div className="space-y-6">
    {/* Hero Stats */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        title="Total Revenue"
        value={salesData.totalSales}
        isLoading={isLoading}
        formatValue={formatRupiah}
        icon={DollarSign}
        trend="+15.8% dari bulan lalu"
        gradient="from-emerald-600 via-emerald-700 to-emerald-800"
      />
      
      <StatCard
        title="Net Profit"
        value={salesData.totalProfit}
        isLoading={false}
        formatValue={formatRupiah}
        icon={TrendingUp}
        trend={`ROI: ${avgROI.toFixed(1)}%`}
        gradient="from-blue-600 via-blue-700 to-blue-800"
      />
      
      <StatCard
        title="Total Assets"
        value={totalAssets}
        isLoading={false}
        formatValue={formatRupiah}
        icon={Package}
        trend={`${filteredCompanies.length} Locations`}
        gradient="from-amber-600 via-amber-700 to-amber-800"
      />
      
      <StatCard
        title="Workforce"
        value={totalEmployees}
        isLoading={false}
        formatValue={formatNumber}
        icon={Users}
        trend={`ESG Score: ${avgESGScore.toFixed(0)}/100`}
        gradient="from-purple-600 via-purple-700 to-purple-800"
      />
    </div>

    {/* Advanced Analytics Grid */}
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      {/* Performance Trend Chart */}
      <div className="xl:col-span-2 bg-white/80 backdrop-blur-lg p-4 rounded-xl shadow-lg border border-white/20">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800 mb-2 sm:mb-0">Performance Trend Analysis</h3>
          <div className="flex items-center space-x-2">
            <ChartLegend color="emerald-500" label="Sales" />
            <ChartLegend color="blue-500" label="Profit" />
            <ChartLegend color="amber-500" label="Target" />
          </div>
        </div>
        <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
          <AreaChart data={salesTrendData}>
            <defs>
              <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0891b2" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#0891b2" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" tick={{fontSize: 10}} />
            <YAxis tickFormatter={(value) => `${(value / 1000000).toFixed(0)}M`} tick={{fontSize: 10}} />
            <Tooltip formatter={(value) => formatRupiah(value)} />
            <Area type="monotone" dataKey="sales" stroke="#059669" strokeWidth={2} fillOpacity={1} fill="url(#salesGradient)" />
            <Area type="monotone" dataKey="profit" stroke="#0891b2" strokeWidth={2} fillOpacity={1} fill="url(#profitGradient)" />
            <Line type="monotone" dataKey="target" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="5 5" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Risk Assessment Radar */}
      <div className="bg-white/80 backdrop-blur-lg p-4 rounded-xl shadow-lg border border-white/20">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Risk Assessment Matrix</h3>
        <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
          <RadarChart data={riskAnalysisData}>
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis dataKey="subject" tick={{fontSize: 9}} />
            <PolarRadiusAxis angle={45} domain={[0, 100]} tick={{fontSize: 9}} />
            <Radar name="Current" dataKey="A" stroke="#059669" fill="#059669" fillOpacity={0.2} strokeWidth={1.5} />
            <Radar name="Benchmark" dataKey="B" stroke="#7c2d12" fill="#7c2d12" fillOpacity={0.2} strokeWidth={1.5} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>

    {/* Portfolio & City Performance */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Portfolio Distribution */}
      <div className="bg-white/80 backdrop-blur-lg p-4 rounded-xl shadow-lg border border-white/20">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Portfolio Distribution</h3>
        <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
          <PieChart>
            <Pie
              data={portfolioDistribution}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}\n${(percent * 100).toFixed(0)}%`}
              outerRadius={isMobile ? 70 : 80}
              fill="#8884d8"
              dataKey="value"
            >
              {portfolioDistribution.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Top Performers */}
      <div className="bg-white/80 backdrop-blur-lg p-4 rounded-xl shadow-lg border border-white/20">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Top Performing Locations</h3>
        <div className="space-y-3">
          {filteredCompanies
            .sort((a, b) => b.roi - a.roi)
            .slice(0, 5)
            .map((company, index) => (
              <div key={company.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg border border-emerald-100">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{company.name}</p>
                    <p className="text-xs text-gray-600">{company.city}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-emerald-600 text-sm">{company.roi.toFixed(1)}% ROI</p>
                  <p className="text-xs text-gray-600">{formatRupiah(company.sales)}</p>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  </div>
);

const CompaniesView = ({ filteredCompanies, isLoadingCompanies, businessId, refreshCompaniesData, formatRupiah, getStatusColor }) => (
  <div className="space-y-6">
    <div className="bg-white/80 backdrop-blur-lg p-4 rounded-xl shadow-lg border border-white/20">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800 mb-2 sm:mb-0">Locations Performance</h2>
        <div className="flex items-center space-x-2">
          <div className="text-xs text-gray-600">
            Showing {filteredCompanies.length} locations
          </div>
          <button 
            onClick={refreshCompaniesData}
            className="flex items-center space-x-1 px-2 py-1 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-md hover:from-emerald-700 hover:to-blue-700 transition-all duration-300 text-xs"
          >
            <RefreshCw className={`w-3 h-3 ${isLoadingCompanies ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>
      
      {/* Loading State */}
      {isLoadingCompanies ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin mx-auto mb-2" />
            <p className="text-gray-600 text-sm">Loading locations data...</p>
          </div>
        </div>
      ) : (
        /* Companies Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredCompanies.slice(0, 12).map((company) => (
            <CompanyCard 
              key={company.id} 
              company={company} 
              formatRupiah={formatRupiah} 
              getStatusColor={getStatusColor} 
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoadingCompanies && filteredCompanies.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Locations Found</h3>
          <p className="text-gray-500 text-sm">
            {businessId ? 'No locations available for this business.' : 'Please select a business first.'}
          </p>
        </div>
      )}

      {/* Load More Button */}
      {filteredCompanies.length > 12 && (
        <div className="flex justify-center mt-6">
          <button className="bg-gradient-to-r from-emerald-600 to-blue-600 text-white py-2 px-6 rounded-lg hover:from-emerald-700 hover:to-blue-700 transition-all duration-300 text-sm font-medium">
            Load More Locations
          </button>
        </div>
      )}
    </div>
  </div>
);

const RiskView = ({ filteredCompanies, formatRupiah, getRiskColor }) => (
  <div className="space-y-6">
    <div className="bg-white/80 backdrop-blur-lg p-4 rounded-xl shadow-lg border border-white/20">
      <h2 className="text-xl font-bold text-gray-800 mb-4">Risk Management Dashboard</h2>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <RiskStatCard
          title="High Risk Locations"
          count={filteredCompanies.filter(c => c.riskLevel === 'High').length}
          icon={AlertTriangle}
          gradient="from-red-500 to-red-600"
        />
        
        <RiskStatCard
          title="Medium Risk Locations"
          count={filteredCompanies.filter(c => c.riskLevel === 'Medium').length}
          icon={Eye}
          gradient="from-amber-500 to-amber-600"
        />
        
        <RiskStatCard
          title="Low Risk Locations"
          count={filteredCompanies.filter(c => c.riskLevel === 'Low').length}
          icon={CheckCircle}
          gradient="from-emerald-500 to-emerald-600"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-3 font-semibold text-gray-700 text-sm">Location</th>
              <th className="text-left py-2 px-3 font-semibold text-gray-700 text-sm">Risk Level</th>
              <th className="text-left py-2 px-3 font-semibold text-gray-700 text-sm">Cash Flow</th>
              <th className="text-left py-2 px-3 font-semibold text-gray-700 text-sm">Compliance</th>
              <th className="text-left py-2 px-3 font-semibold text-gray-700 text-sm">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredCompanies.slice(0, 10).map((company) => (
              <tr key={company.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{company.name}</p>
                    <p className="text-xs text-gray-500">{company.city}</p>
                  </div>
                </td>
                <td className="py-2 px-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(company.riskLevel)}`}>
                    {company.riskLevel}
                  </span>
                </td>
                <td className="py-2 px-3 text-gray-900 text-sm">{formatRupiah(company.cashFlow)}</td>
                <td className="py-2 px-3">
                  <div className="flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mr-2">
                      <div 
                        className="bg-emerald-600 h-1.5 rounded-full" 
                        style={{width: `${company.compliance}%`}}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-600">{company.compliance}%</span>
                  </div>
                </td>
                <td className="py-2 px-3">
                  {company.riskLevel === 'High' && (
                    <span className="text-red-600 text-xs font-medium">Review</span>
                  )}
                  {company.riskLevel === 'Medium' && (
                    <span className="text-amber-600 text-xs font-medium">Monitor</span>
                  )}
                  {company.riskLevel === 'Low' && (
                    <span className="text-emerald-600 text-xs font-medium">Routine</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Small Reusable Components
const StatCard = ({ title, value, isLoading, formatValue, icon: Icon, trend, gradient }) => (
  <div className={`group relative bg-gradient-to-br ${gradient} p-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-[1.02] overflow-hidden`}>
    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-12 translate-x-12"></div>
    <div className="relative z-10">
      <div className="flex items-center justify-between mb-3">
        <Icon className="w-6 h-6 text-white" />
        <span className="text-white/80 text-xs font-medium">{title}</span>
      </div>
      <p className="text-xl sm:text-2xl font-bold text-white mb-1">
        {isLoading ? (
          <div className="flex items-center space-x-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : (
          formatValue(value)
        )}
      </p>
      <div className="flex items-center text-white/80 text-xs">
        <TrendingUp className="w-3 h-3 mr-1" />
        <span>{trend}</span>
      </div>
    </div>
  </div>
);

const ChartLegend = ({ color, label }) => (
  <div className="flex items-center">
    <div className={`w-2 h-2 bg-${color} rounded-full mr-1`}></div>
    <span className="text-xs text-gray-600">{label}</span>
  </div>
);

const CompanyCard = ({ company, formatRupiah, getStatusColor }) => (
  <div className="bg-gradient-to-br from-white to-gray-50 p-4 rounded-lg shadow border border-gray-200 hover:shadow-md transition-all duration-300 hover:scale-[1.02]">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-bold text-gray-800 text-sm truncate" title={company.name}>
        {company.name}
      </h3>
      <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(company.status)}`}>
        {company.status}
      </span>
    </div>
    
    <div className="space-y-2">
      <CompanyDetail label="Location:" value={company.city} />
      <CompanyDetail label="Sales:" value={formatRupiah(company.sales)} valueClass="text-emerald-600" />
      <CompanyDetail label="ROI:" value={`${company.roi.toFixed(1)}%`} valueClass="text-blue-600" />
      <CompanyDetail label="Employees:" value={company.employees} valueClass="text-purple-600" />
      <CompanyDetail label="ESG Score:" value={`${company.esgScore}/100`} valueClass="text-amber-600" />
    </div>
    
    <div className="mt-3 pt-2 border-t border-gray-200">
      <button className="w-full bg-gradient-to-r from-emerald-600 to-blue-600 text-white py-1.5 px-3 rounded-md hover:from-emerald-700 hover:to-blue-700 transition-all duration-300 text-xs font-medium">
        View Details
      </button>
    </div>
  </div>
);

const CompanyDetail = ({ label, value, valueClass = "text-gray-700" }) => (
  <div className="flex justify-between">
    <span className="text-gray-600 text-xs">{label}</span>
    <span className={`font-semibold text-xs ${valueClass}`}>{value}</span>
  </div>
);

const RiskStatCard = ({ title, count, icon: Icon, gradient }) => (
  <div className={`bg-gradient-to-br ${gradient} p-4 rounded-lg text-white`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-white/80 text-xs">{title}</p>
        <p className="text-xl sm:text-2xl font-bold">{count}</p>
      </div>
      <Icon className="w-6 h-6 text-white/80" />
    </div>
  </div>
);

export default PremiumIntegratedDashboard;